import { FeatureFlag, Site } from '@ps/web-core-enums';
import classNames from 'clsx';
import React, { useCallback, useEffect, useState } from 'react';
import { useField } from 'react-final-form';
import { useSelector } from 'react-redux';

import type { SelectOption } from '@swf/components/Inputs/components/DropDown/DropDown';
import Translation from '@swf/components/Translation/Translation';
import { Country as CountryEnum } from '@swf/core/enums';
import useFeature from '@swf/core/hooks/useFeature';
import { select } from '@swf/core/store';
import { required } from '@swf/core/utils/validators';

import { getFrenchCitiesByState } from '../../../../../../utils/placeOfBirth';
import Address from '../../../../../Address/Address';
import City from '../../../../../City/City';
import Country from '../../../../../Country/Country';
import InfoPrompt from '../../../../../InfoPrompt/InfoPrompt';
import PostalCode from '../../../../../PostalCode/PostalCode';
import { findState } from '../../../../../State/State';
import { getCountryStates } from '../../../../../StateSelect/StateSelect';
import RMFDropDown from '../../../RMFDropDown/RMFDropDown';

import {
  TXTACL_Please_Select,
  TXTCLI_City,
  TXTWCL_Country_cannot_be_changed,
  TXTWCL_Please_select_City,
  TXTWCL_You_must_be_a_resident_of_Onta_ELL,
  TXTWCL_You_must_be_a_resident_of_this_ELL
} from './Fields.messages.json';
import {
  info,
  infoText,
  inputText,
  manual,
  manualDropdown,
  manualHidden,
  manualVisible
} from './Fields.scss';
import State from './components/StateField';

interface Props {
  formVisible: boolean;
  addressSearchFeature: boolean;
}

const Fields: React.FC<Props> = ({ addressSearchFeature, formVisible }) => {
  const [cityOptions, setCityOptions] = useState<SelectOption[]>([]);
  const signupCountry = useSelector(select.account.getCountry);
  const isAutofocusEnabled = useFeature(FeatureFlag.SwcSignupRmfAutofocus);

  const {
    input: { value: country }
  } = useField('country', { initialValue: signupCountry });
  const siteId = useSelector(select.cms.getSiteId);
  const isCAON = siteId === Site.CAON;
  const {
    input: { value: state }
  } = useField('state', { initialValue: isCAON ? 'ON' : '' });
  const isFR = siteId === Site.FR && country === CountryEnum.France;
  const showPrompt = [Site.CAON, Site.RO].includes(siteId) || isFR;

  const setFrenchCities = useCallback(async () => {
    const frenchCitiesByState = await getFrenchCitiesByState(
      findState(state, getCountryStates(country))?.value ?? ''
    );
    setCityOptions(frenchCitiesByState);
  }, [country, state]);

  useEffect(() => {
    if (isFR) setFrenchCities();
  }, [isFR, setFrenchCities]);

  return (
    <fieldset
      className={classNames(
        manual,
        formVisible || !addressSearchFeature ? manualVisible : manualHidden,
        { [manualDropdown]: isFR }
      )}
      data-testid="address-form"
    >
      <Country
        country={country}
        fieldName="country"
        isRmfFlow={true}
        labelClassName={classNames({ [info]: showPrompt })}
        labelContent={
          showPrompt ? (
            <InfoPrompt title={{ translation: TXTWCL_Country_cannot_be_changed }}>
              <p className={infoText}>
                <Translation
                  {...(isCAON
                    ? TXTWCL_You_must_be_a_resident_of_Onta_ELL
                    : TXTWCL_You_must_be_a_resident_of_this_ELL)}
                />
              </p>
            </InfoPrompt>
          ) : null
        }
        className={classNames({ [inputText]: showPrompt })}
      />
      <section>
        <Address
          country={country}
          fieldName="streetAddress"
          autofocus={(formVisible || !addressSearchFeature) && isAutofocusEnabled}
        />
        {isFR ? (
          <>
            <State country={country} fieldName="state" />
            <RMFDropDown
              id="city"
              label={TXTCLI_City}
              options={cityOptions}
              validators={[required(TXTWCL_Please_select_City)]}
              placeholder={<Translation {...TXTACL_Please_Select} />}
              disabled={!cityOptions?.length}
              showPlaceholder
            />
          </>
        ) : (
          <>
            <City fieldName="city" />
            <State country={country} fieldName="state" />
          </>
        )}
        <PostalCode country={country} fieldName="zip" />
      </section>
    </fieldset>
  );
};

export default Fields;
